import React, { useEffect, useRef, useState } from "react";
import { N8 } from "@/components/BrandMark";
import { Mic, Volume2, VolumeX, Play, Pause, Loader2, User, User2, Sparkles, Moon } from "lucide-react";
import {
  canSpeak, speak, stopSpeaking, getSelectedVoiceNames,
} from "@/lib/readAloud";
import { getVoicePref, setVoicePref } from "@/lib/voicePref";
import { API } from "@/lib/api";

/**
 * N8VoiceSettings — the "N8 Voice" card on the Profile page.
 *
 * Lets the member turn N8's voice on/off and choose between a soft
 * female voice and a warm male voice. The chosen preference is used by
 * every read-aloud surface in the app (Affirmations, Today's Intention,
 * Quiet Reads, Visualizations, N8 Wisdom) via the shared voicePref +
 * readAloud modules.
 *
 * Below the choice we show which system voice will actually play (e.g.
 * "Samantha (Enhanced)" on macOS) so members can tell at a glance
 * whether their device has a high-quality neural voice available.
 */
const PREVIEW_LINE =
  "Take a slow breath in… and fully release. You are exactly where you need to be.";

export default function N8VoiceSettings() {
  const [pref, setPref] = useState(() => getVoicePref());
  const [names, setNames] = useState({ female: null, male: null });
  const [previewing, setPreviewing] = useState(false);

  // Discover the actual system voices we'd use for each gender so we
  // can show the label ("Samantha (Enhanced)" etc).
  useEffect(() => {
    let cancelled = false;
    getSelectedVoiceNames().then((n) => {
      if (!cancelled) setNames(n || { female: null, male: null });
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => stopSpeaking(), []);

  const supported = canSpeak();

  const toggleEnabled = () => {
    stopSpeaking();
    setPreviewing(false);
    const next = setVoicePref({ enabled: !pref.enabled });
    setPref(next);
  };

  const chooseGender = (gender) => {
    stopSpeaking();
    setPreviewing(false);
    const next = setVoicePref({ gender, enabled: true });
    setPref(next);
  };

  const preview = () => {
    if (previewing) {
      stopSpeaking();
      setPreviewing(false);
      return;
    }
    const ok = speak(PREVIEW_LINE, {
      onEnd: () => setPreviewing(false),
    });
    if (ok) setPreviewing(true);
  };

  const chosenName =
    pref.gender === "male" ? names.male : names.female;

  return (
    <section
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3"
      data-testid="n8-voice-section"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
        <Mic size={12} /> <N8 /> Voice
      </div>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <div className="font-display text-2xl">
            {pref.enabled ? "N8 speaks with you" : "N8's voice is quiet"}
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">
            Affirmations, today's intention, quiet reads, visualizations and
            every N8 recommendation can be read aloud in a soft, natural voice.
            Choose the voice that feels most grounding to you.
          </p>
        </div>

        <button
          type="button"
          onClick={toggleEnabled}
          data-testid="btn-toggle-n8-voice"
          aria-pressed={pref.enabled}
          disabled={!supported}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline tab-link disabled:opacity-50 ${
            pref.enabled
              ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
              : "hover:bg-[hsl(var(--sand))]"
          }`}
        >
          {pref.enabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          {pref.enabled ? "Voice on" : "Voice off"}
        </button>
      </div>

      {/* Gender picker + preview */}
      <div className={`mt-6 grid gap-4 sm:grid-cols-2 ${pref.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <VoiceChoice
          Icon={User2}
          label="Warm female"
          subtitle={names.female || "Best available on your device"}
          active={pref.gender === "female"}
          onClick={() => chooseGender("female")}
          testid="n8-voice-female"
        />
        <VoiceChoice
          Icon={User}
          label="Warm male"
          subtitle={names.male || "Best available on your device"}
          active={pref.gender === "male"}
          onClick={() => chooseGender("male")}
          testid="n8-voice-male"
        />
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={preview}
          disabled={!supported || !pref.enabled}
          data-testid="btn-preview-n8-voice"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.22em] border hairline bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))] tab-link disabled:opacity-50"
        >
          {previewing ? <VolumeX size={12} /> : <Play size={12} />}
          {previewing ? "Stop preview" : "Hear the voice"}
        </button>
        {chosenName && pref.enabled && (
          <span
            className="text-[11px] text-[hsl(var(--muted-foreground))] italic"
            data-testid="n8-voice-selected-name"
          >
            Currently: <span className="text-[hsl(var(--fg))]">{chosenName}</span>
          </span>
        )}
        {!supported && (
          <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic">
            This browser doesn't support in-app voice.
          </span>
        )}
      </div>

      {supported && pref.enabled && !chosenName && (
        <p className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))] italic">
          Your device only offers a system-default voice. On iOS/macOS you
          can enable an "Enhanced" voice under Settings → Accessibility →
          Spoken Content to make N8 sound noticeably softer.
        </p>
      )}

      {/* Preview each ElevenLabs voice — four short samples so members
          can hear exactly which voice will read them each surface
          before choosing. Server routes the audio through /api/tts. */}
      <VoicePreviewGrid />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Voice preview — hear each of the four ElevenLabs voices before choosing.
//
// The app uses TWO voice pairs:
//   * N8 Guide voices (context=n8)         → Ivanna (♀), Jon (♂)
//     — used for Affirmations, Today's Intention, Quiet Reads, Guide.
//   * Meditation voices (context=meditation) → Kristen (♀), Locke (♂)
//     — used for the 12-card guided-meditation library and visualizations.
//
// Each card streams a short line from /api/tts on demand, with only one
// preview allowed to play at a time.

const VOICE_PREVIEWS = [
  {
    id: "n8-female",
    context: "n8",
    gender: "female",
    name: "Ivanna",
    role: "N8 Guide",
    Icon: Sparkles,
    line: "Take a slow breath in… and fully release. You are exactly where you need to be.",
  },
  {
    id: "n8-male",
    context: "n8",
    gender: "male",
    name: "Jon",
    role: "N8 Guide",
    Icon: Sparkles,
    line: "Take a slow breath in… and fully release. You are exactly where you need to be.",
  },
  {
    id: "meditation-female",
    context: "meditation",
    gender: "female",
    name: "Kristen",
    role: "Meditation",
    Icon: Moon,
    line: "Feel your breath moving through your body. Soften. Settle. Return.",
  },
  {
    id: "meditation-male",
    context: "meditation",
    gender: "male",
    name: "Locke",
    role: "Meditation",
    Icon: Moon,
    line: "Feel your breath moving through your body. Soften. Settle. Return.",
  },
];

function VoicePreviewGrid() {
  // Only one preview at a time. `playing` holds the id of the card that
  // is currently playing (or null). `loading` mirrors it while the mp3
  // stream is being fetched so the play icon can turn into a spinner.
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    // Clean up any in-flight preview when the settings card unmounts.
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
  }, []);

  const stop = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
    setPlaying(null);
    setLoading(null);
  };

  const play = (v) => {
    stop();
    setLoading(v.id);
    const url =
      `${API}/tts?context=${encodeURIComponent(v.context)}` +
      `&gender=${encodeURIComponent(v.gender)}` +
      `&text=${encodeURIComponent(v.line)}`;
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.volume = 0.9;
    // Meditation · female is sped up 1.30× client-side to match Locke's
    // tempo (see readAloud.js). Match that here so the preview reflects
    // how it will actually sound during a meditation session.
    if (v.context === "meditation" && v.gender === "female") {
      audio.playbackRate = 1.30;
      audio.preservesPitch = true;
      audio.mozPreservesPitch = true;
      audio.webkitPreservesPitch = true;
    }
    audio.onplaying = () => { setLoading(null); setPlaying(v.id); };
    audio.onended   = () => { setPlaying(null); audioRef.current = null; };
    audio.onerror   = () => { setLoading(null); setPlaying(null); audioRef.current = null; };
    audioRef.current = audio;
    audio.play().catch(() => {
      setLoading(null);
      setPlaying(null);
      audioRef.current = null;
    });
  };

  return (
    <div className="mt-8" data-testid="voice-preview-grid">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">
            Preview each voice
          </div>
          <p className="mt-1 text-[13px] text-[hsl(var(--muted-foreground))] max-w-xl">
            Hear a short sample from each of the four ElevenLabs voices. Tap any card to preview — only one plays at a time.
          </p>
        </div>
        {playing && (
          <button
            type="button"
            onClick={stop}
            data-testid="voice-preview-stop"
            className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] inline-flex items-center gap-1 tab-link"
          >
            <Pause size={12} /> Stop
          </button>
        )}
      </div>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {VOICE_PREVIEWS.map((v) => {
          const isPlaying = playing === v.id;
          const isLoading = loading === v.id;
          return (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => (isPlaying ? stop() : play(v))}
                data-testid={`voice-preview-${v.id}`}
                aria-pressed={isPlaying}
                aria-label={`Preview ${v.name}, the ${v.role} ${v.gender} voice`}
                className={`w-full text-left rounded-md border hairline p-4 tab-link transition-colors ${
                  isPlaying
                    ? "bg-[hsl(var(--terracotta))]/10 border-[hsl(var(--terracotta))]/50"
                    : "bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))]/40"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className={`grid place-items-center w-11 h-11 rounded-full ${
                      isPlaying
                        ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]"
                        : "bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : isPlaying ? (
                      <Pause size={16} />
                    ) : (
                      <Play size={16} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <v.Icon size={11} className="opacity-70" />
                      <span className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                        {v.role} · {v.gender === "female" ? "female" : "male"}
                      </span>
                    </div>
                    <div className={`text-[15px] font-medium mt-0.5 ${isPlaying ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--fg))]"}`}>
                      {v.name}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] italic truncate">
                      "{v.line}"
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function VoiceChoice({ Icon, label, subtitle, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={`text-left rounded-md border hairline p-4 tab-link transition-colors ${
        active
          ? "bg-[hsl(var(--terracotta))]/8 border-[hsl(var(--terracotta))]/40"
          : "bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))]/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className={`grid place-items-center w-9 h-9 rounded-full ${
            active
              ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]"
              : "bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
          }`}
        >
          <Icon size={16} />
        </span>
        <div className="min-w-0">
          <div className={`text-[13px] font-medium ${active ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--fg))]"}`}>
            {label}
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
            {subtitle}
          </div>
        </div>
      </div>
    </button>
  );
}
